// pages/regional/RegionalOverview.jsx
import React, { useContext, useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { ProductsContext } from "../../context/products.context";
import LeftSidebar from "../../components/LeftSidebar";
import DistrictEnrollmentTable from "../../components/DistrictEnrollmentTable";
import DistrictPathwayMatrix from "../../components/DistrictPathwayMatrix";
import RegionalCompareView from "../../components/RegionalCompareView";
import RegionalStackedCards from "../../components/RegionalStackedCards";
import CipStackedChart from "../../components/CipStackedChart";
import { useRegionalEnrollmentData } from "../../hooks/useRegionalEnrollmentData";

// Regional groupings for ESD 171
const REGIONAL_GROUPS = {
  "Okanogan Area": [
    "Nespelem", "Mansfield", "Oroville", "Pateros", "Methow Valley",
    "Bridgeport", "Brewster", "Tonasket", "Okanogan", "Omak"
  ],
  "Grant County Area": [
    "Wilson Creek", "Coulee-Hartline", "Soap Lake", "Grand Coulee Dam",
    "Warden", "Ephrata", "Quincy", "Moses Lake", "Columbia Basin TSC"
  ],
  "Chelan / Douglas Area": [
    "Entiat", "Waterville", "Manson", "Cascade", "Lake Chelan",
    "Cashmere", "Eastmont", "Wenatchee", "Orondo", "Palisades",
    "Stehekin", "Wenatchee Valley TSC"
  ]
};

// Colors for each regional group
const REGIONAL_COLORS = {
  "Okanogan Area": "#3b82f6",      // blue
  "Grant County Area": "#22c55e",   // green
  "Chelan / Douglas Area": "#f97316" // orange
};

/**
 * Regional Overview Page
 * Two-view architecture:
 * - Overview (default): "What does each district offer?" - inventory, CLNA, grants
 * - Compare: "How do districts compare?" - cross-district pattern detection
 */
const RegionalOverview = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    renton,           // cipEnrolled data
    listSchools,      // Array of school/district names
    filterSession,    // Current filter (cluster or program area)
    nontrad,          // Non-traditional CIP codes
    districtName,     // Name of the district/ESD
  } = useContext(ProductsContext);

  // Map URL param to internal view names
  const viewParamMap = {
    'table': 'table',
    'matrix': 'matrix',
    'compare': 'compare',
    'stacked': 'stacked',
    'trends': 'trends'
  };

  // Get initial view from URL param, default to 'table'
  const urlView = searchParams.get('view');
  const initialView = viewParamMap[urlView] || 'table';

  // Tab state for switching between views
  const [activeView, setActiveView] = useState(initialView);

  // Sync URL when view changes
  useEffect(() => {
    const currentParam = searchParams.get('view');
    if (currentParam !== activeView) {
      setSearchParams({ view: activeView }, { replace: true });
    }
  }, [activeView, searchParams, setSearchParams]);

  // Year index - default to most recent (index 4)
  const yearIndex = 4;

  // Process enrollment data using custom hook
  const {
    schoolData,
    summary,
    cipTotals,
    hasData
  } = useRegionalEnrollmentData({
    cipEnrolled: renton,
    listSchools,
    filterSession,
    nontrad,
    yearIndex,
    districtName
  });

  // Calculate nontrad counts for sidebar (matching SchoolEnrolledGrid pattern)
  const nontradCounts = useMemo(() => {
    if (!renton?.length || !nontrad) return { female: 0, male: 0 };

    // Get CIPs that have enrollment in any school
    const cipsWithEnrollment = new Set();
    renton.forEach(cip => {
      const hasEnrollment = cip.enrolled?.some(school =>
        school.enrollment?.[yearIndex] > 0
      );
      if (hasEnrollment) {
        cipsWithEnrollment.add(cip.cipcode);
      }
    });

    return {
      female: nontrad.female?.filter(code => cipsWithEnrollment.has(code)).length ?? 0,
      male: nontrad.male?.filter(code => cipsWithEnrollment.has(code)).length ?? 0
    };
  }, [renton, nontrad, yearIndex]);

  // Loading state
  if (!hasData) {
    return (
      <div className="max-w-[2000px] pt-4">
        <div className="mx-auto px-4">
          <div className="flex items-center justify-center h-96">
            <div className="text-center text-zinc-500">
              <p className="text-lg font-medium">Loading enrollment data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Tab configuration
  const tabs = [
    { id: 'compare', label: 'Regions', subtitle: 'Expandable region sections' },
    { id: 'table', label: 'Districts', subtitle: 'Enrollment by district' },
    { id: 'matrix', label: 'Matrix', subtitle: 'Districts × Pathways' },
    { id: 'stacked', label: 'Charts', subtitle: 'Stacked bar visualization' },
  ];

  // Empty state component
  const EmptyState = () => (
    <div className="bg-white rounded-lg shadow-sm border border-zinc-200 p-12 text-center">
      <div className="text-zinc-400 mb-4">
        <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-zinc-700 mb-2">
        Select a Program Area or Cluster
      </h3>
      <p className="text-sm text-zinc-500">
        Choose a category from the sidebar to view enrollment data.
      </p>
    </div>
  );

  return (
    <div className="max-w-[2000px] pt-4">
      <div className="mx-auto px-4">
        {/* Fixed Left Sidebar - always shown */}
        <div className="bg-white border-gray-100 border-r-2 fixed z-20 mt-10 inset-0 top-[3.8125rem] left-4 right-auto w-[19.5rem] pb-10 px-8 overflow-y-auto">
          <LeftSidebar
            nontradFemaleCount={nontradCounts.female}
            nontradMaleCount={nontradCounts.male}
          />
        </div>

        {/* Main Content - with sidebar offset */}
        <div className="bg-white min-h-screen pl-[360px]">
          {/* Header */}
          <header className="mb-4">
            <h1 className="mt-2 text-3xl font-bold text-slate-900">
              Overview
            </h1>
            <h4 className="text-lg font-medium leading-8 text-slate-600">
              {filterSession
                ? `Enrollment by school/district within ${filterSession}`
                : "Select a cluster or program area from the sidebar"}
            </h4>
          </header>

          {/* View Tabs */}
          <div className="mb-6 border-b border-gray-200">
            <nav className="-mb-px flex space-x-6 overflow-x-auto">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveView(tab.id)}
                  className={`group py-3 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                    activeView === tab.id
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <span>{tab.label}</span>
                  <span className={`block text-xs font-normal mt-0.5 ${
                    activeView === tab.id ? "text-indigo-400" : "text-gray-400"
                  }`}>
                    {tab.subtitle}
                  </span>
                </button>
              ))}
            </nav>
          </div>

          {/* View Content */}
          {!filterSession ? (
            <EmptyState />
          ) : (
            <>
              {activeView === 'table' && (
                <DistrictEnrollmentTable
                  schoolData={schoolData}
                  summary={summary}
                  cipTotals={cipTotals}
                  districtName={districtName}
                  filterSession={filterSession}
                  regionalGroups={REGIONAL_GROUPS}
                />
              )}
              {activeView === 'matrix' && (
                <DistrictPathwayMatrix
                  cipEnrolled={renton}
                  filterSession={filterSession}
                  nontrad={nontrad}
                  yearIndex={yearIndex}
                  regionalGroups={REGIONAL_GROUPS}
                  regionalColors={REGIONAL_COLORS}
                />
              )}
              {activeView === 'compare' && (
                <RegionalCompareView
                  cipEnrolled={renton}
                  filterSession={filterSession}
                  nontrad={nontrad}
                  yearIndex={yearIndex}
                  regionalGroups={REGIONAL_GROUPS}
                  regionalColors={REGIONAL_COLORS}
                />
              )}
              {activeView === 'stacked' && (
                <RegionalStackedCards
                  cipEnrolled={renton}
                  filterSession={filterSession}
                  nontrad={nontrad}
                  yearIndex={yearIndex}
                  regionalGroups={REGIONAL_GROUPS}
                  regionalColors={REGIONAL_COLORS}
                />
              )}
              {activeView === 'trends' && (
                <CipStackedChart
                  cipEnrolled={renton}
                  listSchools={listSchools}
                  filterSession={filterSession}
                  nontrad={nontrad}
                  yearIndex={yearIndex}
                  districtName={districtName}
                  regionalGroups={REGIONAL_GROUPS}
                  regionalColors={REGIONAL_COLORS}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegionalOverview;
